import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { CalendarPlus, CheckCircle2, Play } from "lucide-react";

import { inventoryService, type Asset, type MaintenanceRecord } from "../../services/inventoryService";
import { apiErrorMessage } from "../../utils/apiError";
import { todayIso } from "../../utils/dates";
import AIAssistField from "../AIAssistField";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { errorLine } from "./InventoryParts";
import { MAINTENANCE_TYPES, day, isGone, labelFrom, maintenanceConfig, maintenanceStatus, money } from "./inventoryLogic";

/** Booking a service or repair for an asset. */
export function MaintenanceForm({ assets, asset: fixedAsset, onClose, onDone }: { assets: Asset[]; asset?: Asset; onClose: () => void; onDone: (row: MaintenanceRecord) => void }) {
  const usable = assets.filter((row) => !isGone(row));
  const [assetId, setAssetId] = useState(fixedAsset ? String(fixedAsset.id) : "");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const asset = fixedAsset ?? usable.find((row) => String(row.id) === assetId);
  const save = useMutation({ mutationFn: inventoryService.createMaintenanceRecord, onSuccess: onDone });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const text = (name: string) => String(data.get(name) ?? "").trim();
    save.mutate({
      asset: asset?.id,
      maintenance_type: text("maintenance_type") as MaintenanceRecord["maintenance_type"],
      title: title.trim(),
      description: description.trim(),
      performed_by: text("performed_by") || null,
      scheduled_date: text("scheduled_date"),
      next_maintenance_date: text("next_date") || null,
      labor_cost: text("labor_cost") || "0",
      parts_cost: text("parts_cost") || "0",
    });
  };
  return (
    <FormModal
      id="asset-maintenance"
      title={fixedAsset ? `Book work on ${fixedAsset.name}` : "Book a service or repair"}
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The work could not be booked.") : undefined}
      saveLabel="Book it"
      note="The asset shows as being repaired once the work starts."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The work" divided={false}>
        {!fixedAsset && (
          <Field label="Asset" className="sm:col-span-2">
            <select required value={assetId} onChange={(event) => setAssetId(event.target.value)} className="input mt-1">
              <option value="">Choose an asset</option>
              {usable.map((row) => <option key={row.id} value={row.id}>{row.asset_tag} · {row.name}{row.location ? ` · ${row.location}` : ""}</option>)}
            </select>
          </Field>
        )}
        <Field label="Kind">
          <select name="maintenance_type" defaultValue="corrective" className="input mt-1">
            {MAINTENANCE_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field label="Booked for">
          <input required type="date" name="scheduled_date" min={asset?.purchase_date} defaultValue={todayIso()} className="input mt-1" />
        </Field>
        <Field label="Title" className="sm:col-span-2" hint="e.g. Projector bulb replacement.">
          <input required value={title} onChange={(event) => setTitle(event.target.value)} maxLength={200} className="input mt-1" />
        </Field>
        <Field label="What is needed" as="div" className="sm:col-span-2">
          <AIAssistField
            rows={3}
            value={description}
            onChange={(next) => setDescription(next)}
            placeholder="What is wrong or due, and anything the technician should know."
            context={{ key: "inventory.note", params: { kind: "maintenance", id: asset?.id, title } }}
            draftLabel="Help me write it"
            draftHint="Jot what you noticed; it writes it up without guessing the fault."
          />
        </Field>
        <Field label="Done by" optional hint="The technician or company.">
          <input name="performed_by" maxLength={200} className="input mt-1" />
        </Field>
        <Field label="Next service due" optional>
          <input type="date" name="next_date" className="input mt-1" />
        </Field>
        <Field label="Labour (UGX)" optional>
          <input type="number" name="labor_cost" min={0} step="any" className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Parts (UGX)" optional>
          <input type="number" name="parts_cost" min={0} step="any" className="input mt-1 tabular-nums" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Services and repairs on the school's assets, booked, started and signed off. */
export function AssetMaintenancePanel({ records, assets, isLoading, isError, onRetry, onChanged }: {
  records?: MaintenanceRecord[];
  assets: Asset[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onChanged: () => void;
}) {
  const today = todayIso();
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [booking, setBooking] = useState(false);
  const [work, setWork] = useState("");
  const [parts, setParts] = useState("");
  const [advice, setAdvice] = useState("");
  const move = useMutation({
    mutationFn: ({ id, status, details }: { id: number; status: MaintenanceRecord["status"]; details?: Record<string, string> }) => inventoryService.transitionMaintenance(id, status, details),
    onSuccess: (row) => {
      if (row.status !== "in_progress") setViewing(null);
      setNotice({ tone: "success", text: row.status === "in_progress" ? `Work on ${row.asset_name} has started.` : row.status === "completed" ? `${row.asset_name} is back in use.` : `The work on ${row.asset_name} was cancelled.` });
      onChanged();
    },
  });

  const columns: RegisterColumn<MaintenanceRecord>[] = [
    { header: "Booked for", cell: (row) => day(row.scheduled_date) || "—", exportValue: (row) => day(row.scheduled_date), className: "whitespace-nowrap tabular-nums text-gray-700" },
    {
      header: "Asset",
      cell: (row) => (
        <span>
          <span className="font-medium text-gray-900">{row.asset_name}</span>
          <span className="block text-xs text-gray-500">{row.asset_tag}</span>
        </span>
      ),
      exportValue: (row) => `${row.asset_tag} ${row.asset_name}`,
    },
    { header: "Work", cell: (row) => row.title, exportValue: (row) => row.title, className: "min-w-[12rem] whitespace-normal text-gray-700" },
    { header: "Kind", cell: (row) => labelFrom(MAINTENANCE_TYPES, row.maintenance_type), exportValue: (row) => labelFrom(MAINTENANCE_TYPES, row.maintenance_type), className: "text-gray-700" },
    { header: "Done", cell: (row) => day(row.performed_date) || <span className="text-gray-300">—</span>, exportValue: (row) => day(row.performed_date), className: "tabular-nums text-gray-700" },
    { header: "Cost", cell: (row) => money(row.total_cost), exportValue: (row) => Number(row.total_cost), align: "right" },
    { header: "Status", cell: (row) => <StatusDot {...maintenanceStatus(row, today)} />, exportValue: (row) => maintenanceStatus(row, today).label },
  ];

  const record = viewing !== null ? records?.find((row) => row.id === viewing) : undefined;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Asset maintenance"
        description="Open a job to start it, sign it off or cancel it."
        actions={
          <button type="button" disabled={!assets.some((row) => !isGone(row))} onClick={() => setBooking(true)} className="btn-primary h-8 min-h-8 px-3 text-xs disabled:opacity-50">
            <CalendarPlus className="mr-1.5 h-3.5 w-3.5" /> Book work
          </button>
        }
        rows={records}
        config={maintenanceConfig(today)}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          move.reset();
          setWork("");
          setParts("");
          setAdvice("");
          setViewing(row.id);
        }}
        openLabel={(row) => (row.status === "scheduled" ? "Start" : row.status === "in_progress" ? "Sign off" : "Open")}
        noun={["job", "jobs"]}
        exportName={{ file: "asset-maintenance", title: "Asset Maintenance" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No work booked on any asset."
      />

      {record && (
        <DetailsModal
          title={`${record.asset_name} · ${record.title}`}
          headline={{ label: labelFrom(MAINTENANCE_TYPES, record.maintenance_type), value: money(record.total_cost) || "UGX 0", note: `Labour ${money(record.labor_cost) || "UGX 0"} · parts ${money(record.parts_cost) || "UGX 0"}` }}
          status={maintenanceStatus(record, today)}
          facts={[
            ["Asset", `${record.asset_tag} · ${record.asset_name}`],
            ["Booked for", day(record.scheduled_date) || undefined],
            ["Done on", day(record.performed_date) || undefined],
            ["Done by", record.performed_by ?? undefined],
            ["Next service", day(record.next_maintenance_date) || undefined],
            ["Booked by", record.recorded_by_name],
          ]}
          sections={[
            { title: "What was needed", body: record.description },
            { title: "Work done", body: record.work_performed },
            { title: "Parts replaced", body: record.parts_replaced },
            { title: "Advice", body: record.recommendations },
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              {(record.status === "scheduled" || record.status === "in_progress") && (
                <button type="button" disabled={move.isPending} onClick={() => move.mutate({ id: record.id, status: "cancelled" })} className="mr-auto rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50">
                  Cancel the work
                </button>
              )}
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {record.status === "scheduled" && (
                <button type="button" disabled={move.isPending} onClick={() => move.mutate({ id: record.id, status: "in_progress" })} className="btn btn-primary gap-1.5">
                  <Play className="h-4 w-4" /> Work has started
                </button>
              )}
            </>
          }
        >
          {move.isError && errorLine(move.error, "That could not be done.")}
          {record.status === "in_progress" && (
            <section className="space-y-3 rounded-lg border border-gray-200 p-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-900">Sign it off</h4>
                <p className="mt-0.5 text-xs text-gray-500">The asset goes back into use.</p>
              </div>
              <Field label="Work done">
                <textarea required rows={2} value={work} onChange={(event) => setWork(event.target.value)} className="input mt-1" />
              </Field>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Parts replaced" optional>
                  <input value={parts} onChange={(event) => setParts(event.target.value)} className="input mt-1" />
                </Field>
                <Field label="Advice for next time" optional>
                  <input value={advice} onChange={(event) => setAdvice(event.target.value)} className="input mt-1" />
                </Field>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  disabled={!work.trim() || move.isPending}
                  onClick={() => move.mutate({ id: record.id, status: "completed", details: { work_performed: work.trim(), parts_replaced: parts.trim(), recommendations: advice.trim() } })}
                  className="btn btn-primary gap-1.5"
                >
                  <CheckCircle2 className="h-4 w-4" /> Sign it off
                </button>
              </div>
            </section>
          )}
        </DetailsModal>
      )}

      {booking && (
        <MaintenanceForm
          assets={assets}
          onClose={() => setBooking(false)}
          onDone={(saved) => {
            setBooking(false);
            setNotice({ tone: "success", text: `Work on ${saved.asset_name} booked for ${day(saved.scheduled_date)}.` });
            setViewing(saved.id);
            onChanged();
          }}
        />
      )}
    </div>
  );
}
